import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import type { OrganizationApiKeyData } from '@openx/types/organization.ts';
import {
  type AllTableCriteria,
  type CriteriaChange,
  CriteriaDimension,
  SortDirection,
} from '@openx/types/tableCriteria.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateApiKeyModal } from '../hooks/useCreateApiKeyModal.tsx';
import { useGetApiKeysTableConfig } from '../hooks/useGetApiKeysTableConfig.tsx';
import { useGetApiKeysTableData } from '../hooks/useGetApiKeysTableData.ts';

type ApiKeysSectionProps = {
  accountId: string;
  apiKeys: OrganizationApiKeyData[];
  loading: boolean;
  showActiveKeys?: boolean;
};

const initialCriteria: AllTableCriteria = {
  [CriteriaDimension.SORT]: {
    column: 'first_name',
    direction: SortDirection.ASC,
    sortableColumns: ['first_name', 'expires_at', 'created_date'],
  },
};

export const ApiKeysTable = ({ accountId, apiKeys, loading, showActiveKeys }: ApiKeysSectionProps) => {
  const { t } = useTranslation();
  const handleCreateApiKey = useCreateApiKeyModal();
  const [criteria, setCriteria] = useState<AllTableCriteria>(initialCriteria);
  const [activeTablePage, setActiveTablePage] = useState(0);
  const { activeKeystableColumns, expiredKeysTableColumns, rowAction } = useGetApiKeysTableConfig();

  const { apiKeysToDisplayInTable, shouldDisplayLoadMoreButton } = useGetApiKeysTableData({
    activeTablePage,
    apiKeys,
    criteria,
    showActiveKeys,
  });

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension>) => {
      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);
      setCriteria(newCriteriaProcessed);
    },
    [criteria]
  );

  return (
    <Paper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          pl: 3.5,
          pr: 3.5,
        }}
      >
        <Typography data-test="api-keys-list-title" variant="h2">
          {showActiveKeys ? t('API Keys List') : t('Expired API Keys List')}
        </Typography>
        <Button data-test="create-new-api-key-button" color="primary" onClick={() => handleCreateApiKey(accountId)}>
          {t('Create new Api Key')}
        </Button>
      </Box>

      {!apiKeysToDisplayInTable?.length && !loading ? (
        <Typography data-test="no-api-keys-message" variant="body2" color={'textSecondary'} sx={{ p: 2, pl: 3.5 }}>
          {t('No API Keys have been found.')}
        </Typography>
      ) : (
        <Table
          name={TableName.API_KEYS}
          data={apiKeysToDisplayInTable}
          onCriteriaChange={handleCriteriaChange}
          dangerRowAction={rowAction}
          criteria={criteria}
          columns={showActiveKeys ? activeKeystableColumns : expiredKeysTableColumns}
          loading={loading}
          customManagementSection={
            shouldDisplayLoadMoreButton && (
              <Button
                onClick={() => setActiveTablePage(activeTablePage + 1)}
                sx={{ display: 'flex', justifyContent: 'center', margin: '0 auto' }}
                variant="text"
                data-test="show-more-button"
              >
                {t('Show More')}
              </Button>
            )
          }
        />
      )}
    </Paper>
  );
};
